/**
 * AppCard Component
 *
 * Compact card for Home page showing an app's health at a glance.
 * Three metric groups: Activity, Issues, Operations.
 * Multiple cards fit per row in a responsive grid.
 *
 * Props:
 * - app: { app_id, app_name, kpis }
 * - environment: string
 * - timeWindow: string
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, Gauge } from 'lucide-react';
import { palette, components, typography, withOpacity } from '../utils/designTokens';

function AppCard({ app, environment, timeWindow }) {
  const navigate = useNavigate();

  if (!app?.app_id || !app?.app_name) {
    console.error('AppCard: Missing required fields (app_id or app_name)');
    return null;
  }

  const { app_id, app_name, kpis } = app;

  const sessions = kpis?.activity?.total_sessions;
  const traces = kpis?.activity?.total_traces;
  const negativeSignals = kpis?.classifiers?.negative?.total;
  const errors = kpis?.detections?.errors;
  const hasIssues = (negativeSignals || 0) > 0 || (errors || 0) > 0;
  const costUsd = kpis?.cost?.total_cost_usd;
  const p95Ms = kpis?.latency?.p95_total;

  const handleClick = () => {
    navigate(`/app/${app_id}?tab=overview&env=${environment}&range=${timeWindow}`);
  };

  const formatCost = (v) => {
    if (v == null) return '-';
    if (v === 0) return '$0';
    if (v < 0.01) return '<$0.01';
    return `$${v.toFixed(2)}`;
  };

  const formatLatency = (ms) => {
    if (ms == null) return '-';
    if (ms === 0) return '0s';
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatCount = (v) => {
    if (v == null) return '-';
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return v.toLocaleString();
  };

  return (
    <div
      onClick={handleClick}
      className={`${components.card.rounded} cursor-pointer`}
      style={{
        backgroundColor: components.card.backgroundColor,
        border: `1px solid ${palette.borders.strong}`,
        overflow: 'hidden',
        transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'transform, box-shadow, border-color',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = palette.accents.secondary.base;
        e.currentTarget.style.borderWidth = '2px';
        e.currentTarget.style.transform = 'translateY(-4px) scale(1.01)';
        e.currentTarget.style.boxShadow = `${palette.quality.shadowCard}, 0 0 0 1px ${withOpacity(palette.accents.secondary.base, 20)}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = palette.borders.strong;
        e.currentTarget.style.borderWidth = '1px';
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* App Name Header */}
      <div
        className="truncate"
        style={{
          padding: '10px 20px',
          backgroundColor: 'transparent',
        }}
      >
        <h3
          className={`${typography.size.base} ${typography.weight.semibold} truncate`}
          style={{ color: palette.text.heading }}
          title={app_name}
        >
          {app_name}
        </h3>
      </div>

      {/* Three metric groups with centered dividers */}
      <div
        className="flex items-stretch px-4 py-4"
        style={{
          backgroundColor: 'var(--color-panel-primary)',
          borderTop: `1px solid ${palette.borders.strong}`,
        }}
      >
        <div className="flex-1">
          <MetricGroup
            icon={<Activity size={14} />}
            label="Activity"
            color={palette.text.neutral}
            items={[
              { label: 'Sessions', value: formatCount(sessions) },
              { label: 'Traces', value: formatCount(traces) },
            ]}
          />
        </div>

        <div className="mx-4 self-stretch" style={{ width: '1px', backgroundColor: palette.neutrals.borderStrong }} />

        <div className="flex-1">
          <MetricGroup
            icon={<AlertTriangle size={14} />}
            label="Issues"
            color={hasIssues ? palette.status.danger : palette.text.neutral}
            items={[
              { label: 'Signals', value: formatCount(negativeSignals) },
              { label: 'Errors', value: formatCount(errors) },
            ]}
          />
        </div>

        <div className="mx-4 self-stretch" style={{ width: '1px', backgroundColor: palette.neutrals.borderStrong }} />

        <div className="flex-1">
          <MetricGroup
            icon={<Gauge size={14} />}
            label="Operations"
            color={palette.text.neutral}
            items={[
              { label: 'Cost', value: formatCost(costUsd) },
              { label: 'P95', value: formatLatency(p95Ms) },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function MetricGroup({ icon, label, color, items }) {
  return (
    <div className="min-w-0 flex flex-col items-center">
      {/* Group header */}
      <div className="flex items-center gap-1.5 mb-2">
        <span style={{ color }}>{icon}</span>
        <span
          className={`${typography.weight.medium} uppercase tracking-wider`}
          style={{ color, fontSize: '11px' }}
        >
          {label}
        </span>
      </div>
      {/* Metrics */}
      <div className="flex gap-8">
        {items.map((item) => (
          <div key={item.label} className="min-w-0">
            <div
              className={`${typography.size.xs}`}
              style={{ color: palette.text.supporting, marginBottom: '2px' }}
            >
              {item.label}
            </div>
            <div
              className={`${typography.size.base} ${typography.weight.semibold}`}
              style={{ color: palette.text.primary }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AppCard;
